import { V4UUID, workspaceState } from '@/firebase/database';
import { defineStore } from 'pinia';
import { useGlobalStore } from '../globalModule/store';
import {
  processCustomCppNode,
  processCustomHppText
} from './customCodeProcessing/processCppNode';
import {
  processCustomMsgText,
  processCustomSrvText
} from './customCodeProcessing/processCustomInterface';
import {
  processCustomCmakeLists,
  processCustomLaunchFile,
  processCustomPackageXml
} from './customCodeProcessing/processCustomPackageText';
import { processCustomPythonNode } from './customCodeProcessing/processPythonNode';
import {
  ITreeFile,
  ITreeFolder,
  workspaceToFileTree
} from '@/codeGeneration/codeGen';
import { getAllPackagesAsync } from '@/firebase/databaseDependencyInjectables';
export const useCodeViewStore = defineStore({
  id: 'codeView',
  state: (): {
    workspaceId: V4UUID | null;
    fileTreeRoot: ITreeFolder | null;
  } => {
    return { workspaceId: null, fileTreeRoot: null };
  },
  actions: {
    async loadCodeFromPersistance() {
      // loading data
      if (!this.workspaceId) return;
      this.fileTreeRoot =
        workspaceToFileTree(
          this.workspaceId,
          await getAllPackagesAsync(this.workspaceId)
        ) || null;
    },
    /**
     * transfers the given code editor text into storable custom text
     * data, saves to objects.
     * Note: Uses the node canvas store
     * @param file
     * @param editorText
     */
    saveCodeEditorFile(file: ITreeFile, editorText: string) {
      const globalStore = useGlobalStore();
      if (!this.workspaceId) return;
      const elemType = globalStore.getTypeOfElemById(file.elemId);
      if (elemType === 'Node') {
        let node = globalStore.getNode(file.elemId);
        if (!node) return;
        if (file.language === 'python') {
          node.customText = processCustomPythonNode(
            node,
            editorText,
            this.workspaceId
          );
        } else if (file.language === 'cpp') {
          if (file.name.endsWith('.cpp')) {
            node.customText = processCustomCppNode(
              node,
              editorText,
              this.workspaceId
            );
          } else if (file.name.endsWith('.hpp')) {
            node.customText = processCustomHppText(node, editorText);
          }
        }
      } else if (elemType === 'Package') {
        const pkg = globalStore.getPackage(file.elemId);
        if (!pkg) {
          console.warn('Error saving custom code: Package not found');
          return;
        }
        if (file.name.endsWith('.launch.py')) {
          pkg.customText = processCustomLaunchFile(pkg, editorText);
        } else if (file.name === 'package.xml') {
          pkg.customText = processCustomPackageXml(pkg, editorText);
        } else if (file.name === 'CMakeLists.txt') {
          pkg.customText = processCustomCmakeLists(pkg, editorText);
        }
      } else if (elemType === 'Message') {
        const msg = globalStore.getMessage(file.elemId);
        if (!msg) {
          console.warn('Error saving custom code: Message not found');
          return;
        }
        msg.customText = processCustomMsgText(msg, editorText);
      } else if (elemType === 'Service') {
        const srv = globalStore.getService(file.elemId);
        if (!srv) {
          console.warn('Error saving custom code: Service not found');
          return;
        }
        srv.customText = processCustomSrvText(srv, editorText);
      }
      globalStore.saveData();
    }
  }
});
